import * as T from 'components/ui/typography'
import * as React from 'react'

import { Popover, PopoverTrigger } from 'components/ui/popover'

import { PopoverContent } from '@radix-ui/react-popover'
import { useAppConfig } from 'hooks/features/use-features'
import { useCredits, useCreditsAvailable } from 'hooks/swr'
import { UserContext } from 'providers//context/user-context'
import useTranslation from 'next-translate/useTranslation'

import Loader from 'components/shared/loader'
import CreditTooltip from './credit-tooltip'
import { cn } from 'lib/utils'
import { Coins } from 'lucide-react'

const CreditResume = ({ path }: { path: string }) => {
    const [creditsData, setCreditsData] = React.useState<CreditDataTypes>()
    const [noLimit, setNoLimit] = React.useState<boolean>(false)

    const { data, loading, mutate: mutateCredits } = useCredits()
    const { data: availableData, loading: availableLoading, mutate: mutateAvailable } = useCreditsAvailable()
    const { userData } = React.useContext(UserContext)

    React.useEffect(() => {
        mutateCredits()
        mutateAvailable()
    }, [mutateAvailable, mutateCredits])

    const { appConfig } = useAppConfig()

    const hasCreditsFeature = appConfig.feature.includes('credits')
    const { t } = useTranslation('header')

    const credits = React.useMemo(() => {
        if (!availableData?.plan.unlimited) {
            if (path === 'simulations' && availableData?.plan)
                return {
                    creditInPlan: availableData?.plan.simulation,
                    creditOutPlan: availableData?.tokens.simulations,
                    creditPerDay: data?.plan.quiz,
                    totalCredit: availableData?.plan.simulation + availableData?.tokens.simulations,
                }
            if (path === 'quiz' && availableData?.plan)
                return {
                    creditInPlan: availableData?.plan.quiz,
                    creditOutPlan: availableData?.tokens.quizzes,
                    creditPerDay: data?.plan.quiz,
                    totalCredit: availableData?.plan.quiz + availableData?.tokens.quizzes,
                }
            if (path === 'duels' && availableData?.plan)
                return {
                    creditInPlan: availableData?.plan.challenge,
                    creditOutPlan: availableData?.tokens.challenges,
                    creditPerDay: data?.plan.challenge,
                    totalCredit: availableData?.plan.challenge + availableData?.tokens.challenges,
                }
        } else return
    }, [
        availableData?.plan,
        availableData?.tokens.challenges,
        availableData?.tokens.quizzes,
        availableData?.tokens.simulations,
        data?.plan.challenge,
        data?.plan.quiz,
        path,
    ])

    React.useEffect(() => {
        if (credits) {
            setCreditsData(credits)
            setNoLimit(false)
        } else setNoLimit(true)
    }, [availableData, availableLoading, credits, data, loading, userData?.workspaceId])

    const creditAvailableMemo = React.useCallback(() => {
        if (noLimit && !loading) {
            return <T.Large className="capitalize">{t('credit.unlimitedCredits')}</T.Large>
        }

        if (!loading && !availableLoading) {
            const { totalCredit, creditInPlan } = creditsData || {}

            if (hasCreditsFeature && userData?.workspaceId) {
                const color = totalCredit === 0 ? 'text-error' : null
                return (
                    <div className="flex gap-1">
                        <T.Large className={cn(color, 'flex flex-nowrap capitalize')}>
                            <b>
                                {totalCredit} {t('credit.credits')}
                            </b>
                        </T.Large>
                        <T.Large>{t('credit.remained')}</T.Large>
                    </div>
                )
            }

            if (!hasCreditsFeature) {
                const color = creditInPlan === 0 ? 'text-error' : null
                return (
                    <div className="flex gap-1">
                        <T.Large className={cn(color, 'flex flex-nowrap capitalize')}>
                            <b>{t('credit.remainingTest') + `: ${creditInPlan}`}</b>
                        </T.Large>
                    </div>
                )
            }
        }

        return (
            <div className="flex justify-center min-w-[110px]">
                <Loader size={20} />
            </div>
        )
    }, [availableLoading, creditsData, hasCreditsFeature, loading, noLimit, t, userData?.workspaceId])

    return (
        <div className="self-end justify-self-end flex items-center border rounded-lg p-1 py-2 mt-2 sm:mt-0 mr-4">
            <Popover>
                <PopoverTrigger className="flex items-center ">
                    <Coins className="ml-1" />
                    <div className="ml-1 mr-3">{creditAvailableMemo()}</div>
                </PopoverTrigger>
                {hasCreditsFeature ? (
                    <PopoverContent className="bg-white mt-3 mr-2 border z-50 rounded-lg p-3">
                        <CreditTooltip path={path} unlimited={noLimit} creditData={creditsData} />
                    </PopoverContent>
                ) : null}
            </Popover>
        </div>
    )
}

export default CreditResume
